import { useCallback, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useSafeUpdate } from '../hooks/use-safe-update.js';
import { reloadToLatest, useStaleBundle } from '../hooks/use-stale-bundle.js';

/** How often an open tab asks for a new service worker: the browser only does it when a page loads */
const UPDATE_CHECK_MS = 10 * 60 * 1000;

export function PwaPrompt() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => void registration.update().catch(() => undefined);
      window.setInterval(check, UPDATE_CHECK_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
    },
  });
  // Also when the service worker did not say so: the site serves a newer bundle than this page runs
  const newer = useStaleBundle();
  const waiting = needRefresh || newer !== null;

  const [dismissed, setDismissed] = useState(false);
  // A new version loads on its own as soon as it is safe (tab hidden, nothing being typed, no dialog open); the banner
  // below is for the moments it is not
  // Asked, through reloadToLatest even when the service worker said so: updateServiceWorker only messages a waiting
  // one, and does nothing when another tab already activated it
  const apply = useCallback((asked = false) => {
    if (asked) void reloadToLatest(newer ?? 'service-worker', true);
    else if (needRefresh) void updateServiceWorker(true);
    else if (newer) void reloadToLatest(newer);
  }, [needRefresh, newer, updateServiceWorker]);
  useSafeUpdate(waiting, apply);

  if (!waiting || dismissed) return null;

  return (
    <>
      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(1rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
      <div
        className="animate-slide-up fixed bottom-4 right-4 z-[1000] max-w-sm rounded-xl shadow-xl flex items-start gap-3 p-4"
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
        }}
      >
        {/* Sparkles icon */}
        <div className="shrink-0 mt-0.5">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: 'var(--color-brand)' }}
          >
            <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z" />
          </svg>
        </div>

        {/* Text + actions */}
        <div className="flex flex-col gap-2 min-w-0 flex-1">
          <div className="flex flex-col gap-0.5">
            <span
              className="text-sm font-medium"
              style={{ color: 'var(--color-text)' }}
            >
              Update available
            </span>
            <span
              className="text-xs"
              style={{ color: 'var(--color-text-3)' }}
            >
              A new version is ready to install
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="text-xs cursor-pointer border-none bg-transparent px-1 py-0.5"
              style={{ color: 'var(--color-text-3)' }}
            >
              Later
            </button>
            <button
              type="button"
              onClick={() => apply(true)}
              className="text-xs cursor-pointer border-none rounded-md px-3 py-1.5 font-medium text-white"
              style={{ background: 'var(--color-brand)' }}
            >
              Update now
            </button>
          </div>
        </div>

        {/* Dismiss X button */}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="shrink-0 cursor-pointer border-none bg-transparent p-0.5 rounded"
          style={{ color: 'var(--color-text-3)' }}
          aria-label="Dismiss"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </>
  );
}
